import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, Button, Badge, Input, Textarea } from '@components';
import { KnowledgeBase, CreateKnowledgeBaseData } from '../types';

interface KnowledgeBasesGridProps {
  knowledgeBases: KnowledgeBase[];
  onCreate: (data: CreateKnowledgeBaseData) => void;
  onDownload: (id: string) => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  showCreateForm: boolean;
  setShowCreateForm: (show: boolean) => void;
}

export const KnowledgeBasesGrid: React.FC<KnowledgeBasesGridProps> = ({
  knowledgeBases,
  onCreate,
  onDownload,
  onEdit,
  onDelete,
  showCreateForm,
  setShowCreateForm,
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    tags: '',
  });
  const [errors, setErrors] = useState<{ name?: string }>({});

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required fields
    const newErrors: { name?: string } = {};
    if (!formData.name.trim()) {
      newErrors.name =
        t('knowledgeBase.knowledgeBaseName') + ' ' + t('common.required').toLowerCase();
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    onCreate({ name: formData.name.trim(), description: formData.description.trim() });
    setFormData({ name: '', description: '', tags: '' });
    setShowCreateForm(false);
  };

  return (
    <Card>
      <CardContent>
        {showCreateForm && (
          <div className="mb-6 p-6 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <span className="material-icons">add</span>
              {t('knowledgeBase.createNewKnowledgeBase')}
            </h3>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="kb-name"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    {t('knowledgeBase.knowledgeBaseName')}
                  </label>
                  <Input
                    id="kb-name"
                    type="text"
                    value={formData.name}
                    onChange={e => {
                      setFormData({ ...formData, name: e.target.value });
                      if (errors.name) {
                        setErrors({ ...errors, name: undefined });
                      }
                    }}
                    placeholder={t('knowledgeBase.enterKnowledgeBaseName')}
                    required
                    maxLength={100}
                    state={errors.name ? 'error' : 'default'}
                    errorText={errors.name}
                  />
                </div>

                <div>
                  <label
                    htmlFor="kb-tags"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    {t('knowledgeBase.tags')}
                  </label>
                  <Input
                    id="kb-tags"
                    type="text"
                    value={formData.tags}
                    onChange={e => setFormData({ ...formData, tags: e.target.value })}
                    placeholder={t('knowledgeBase.enterTagsSeparatedByCommas')}
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="kb-description"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                  {t('common.description')}
                </label>
                <Textarea
                  id="kb-description"
                  value={formData.description}
                  onChange={e => setFormData({ ...formData, description: e.target.value })}
                  placeholder={t('knowledgeBase.describePurposeAndContent')}
                  rows={3}
                />
              </div>

              <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowCreateForm(false)}
                  className="flex items-center gap-2"
                >
                  <span className="material-icons">close</span>
                  {t('knowledgeBase.cancel')}
                </Button>
                <Button
                  type="submit"
                  variant="gradient-multi"
                  className="flex items-center gap-2"
                  disabled={!formData.name.trim()}
                >
                  <span className="material-icons">add</span>
                  {t('knowledgeBase.createKnowledgeBase')}
                </Button>
              </div>
            </form>
          </div>
        )}

        {knowledgeBases.length === 0 ? (
          <div className="px-4 py-12">
            <div className="text-center space-y-4">
              <div className="mx-auto w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center">
                <span className="material-icons text-2xl text-gray-400 dark:text-gray-500">
                  folder
                </span>
              </div>
              <div className="space-y-2">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {t('knowledgeBase.noKnowledgeBasesFound')}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm mx-auto">
                  {t('knowledgeBase.createKnowledgeBaseDescription')}
                </p>
              </div>
              <div className="pt-2">
                <Button
                  variant="gradient-multi"
                  size="sm"
                  onClick={() => setShowCreateForm(true)}
                  className="inline-flex items-center gap-2"
                >
                  <span className="material-icons text-sm">add</span>
                  {t('knowledgeBase.createKnowledgeBase')}
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {knowledgeBases.map(kb => (
              <Card
                key={kb.id}
                className="hover:shadow-xl transition-all duration-200 border border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-600"
              >
                <CardContent className="p-4">
                  {/* Header with icon and status */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                        <span className="material-icons text-blue-600 dark:text-blue-400 text-lg">
                          folder
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-base text-gray-900 dark:text-white truncate">
                          {kb.name}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-1">
                          {kb.description}
                        </p>
                      </div>
                    </div>
                    <Badge variant="success" size="sm" className="ml-2">
                      {kb.status}
                    </Badge>
                  </div>

                  {/* Stats and info */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 bg-blue-100 dark:bg-blue-900/30 rounded flex items-center justify-center">
                          <span className="material-icons text-blue-600 dark:text-blue-400 text-sm">
                            description
                          </span>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
                            Documents
                          </p>
                          <p className="text-lg font-bold text-gray-900 dark:text-white">
                            {kb.documentCount}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
                        Last Updated
                      </p>
                      <p className="text-sm font-semibold text-gray-900 dark:text-white">
                        {new Date(kb.modified).toLocaleDateString()}
                      </p>
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-200 dark:border-gray-700">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDownload(kb.id)}
                      className="p-1.5 h-8 w-8 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                      title="Download Knowledge Base"
                    >
                      <span className="material-icons text-base text-gray-600 dark:text-gray-400">
                        download
                      </span>
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onEdit(kb.id)}
                      className="p-1.5 h-8 w-8 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                      title="Edit Knowledge Base"
                    >
                      <span className="material-icons text-base text-gray-600 dark:text-gray-400">
                        edit
                      </span>
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDelete(kb.id)}
                      className="p-1.5 h-8 w-8 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20"
                      title="Delete Knowledge Base"
                    >
                      <span className="material-icons text-base text-red-600">delete</span>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
